import React, { useState, useEffect, useContext } from 'react'
import TextField from '@mui/material/TextField'
import PrimaryButton from '../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from './styles.module.css'
import { applyPromoCode, getOrderData, removePromoCode } from './api'
import CircularProgress from '@mui/material/CircularProgress'
import { fetchData } from '../../../../../../global/utils/fetch'
import { ToastNotificationContext } from '../../../../../../global/context/toast-context/ToastNotificationContext'
import { getPreTaxSubtotal } from './utils'
import { IOrder } from '../../../../../../../../app/entities/Order'
import { IDiscount } from '../../../../../../../../app/entities/Discount'

/**
 * Frontend's simplified version of an order - only includes properties we need
 * for promo code functionality
 */
type OrderData = Pick<IOrder, 'orderServices'> & {
  discounts?: Array<
    Pick<IDiscount, 'type' | 'measure' | 'value' | 'reason' | 'allocations'>
  >
  totalDiscount?: number
  totalPromoCode?: number
  estimatedTotal?: number
}
interface PromoCodeInputProps {
  orderObjectId: string
  disabled?: boolean
  onPromoApplied?: () => void
  servicesData?: Pick<
    OrderData,
    'totalDiscount' | 'totalPromoCode' | 'estimatedTotal' | 'orderServices'
  >
  disableRemovePromo?: boolean
}

function PromoCodeInput(props: PromoCodeInputProps) {
  const {
    orderObjectId,
    disabled,
    onPromoApplied,
    servicesData,
    disableRemovePromo = false,
  } = props
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const [promoCode, setPromoCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null)
  const [initialCheckDone, setInitialCheckDone] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const lastOrderDataRef = React.useRef<OrderData | null>(null)

  // Helper function for showing toast notifications
  const showToast = (
    message: string,
    severity: 'success' | 'error' = 'error',
  ) => {
    setToastStatus({
      ...toastStatus,
      isOpen: true,
      message,
      severity,
    })
  }

  useEffect(() => {
    // Check if an order already has a promo code applied
    const checkExistingPromoCode = async () => {
      if (!orderObjectId) return

      try {
        setIsLoading(true)
        const orderData = await fetchData<OrderData>(
          getOrderData(orderObjectId),
        )
        lastOrderDataRef.current = orderData

        // Only look for explicit PromoCodeDiscount type
        if (orderData.discounts && orderData.discounts.length > 0) {
          const foundPromoCode = orderData.discounts.find(
            (discount) => discount.type === 'PromoCodeDiscount',
          )
          if (foundPromoCode) {
            setAppliedPromoCode(foundPromoCode.reason || 'Unknown Promo')
          }
        }
      } catch (error) {
        console.error('Error checking for existing promo code:', error)
        showToast('Unable to check for existing promo codes')
      } finally {
        setIsLoading(false)
        setInitialCheckDone(true)
      }
    }

    checkExistingPromoCode()
  }, [orderObjectId, initialCheckDone])

  // Clear applied promo code when disabled (when order is marked as free)
  // Refresh promo code status when re-enabled
  useEffect(() => {
    if (disabled && appliedPromoCode) {
      setAppliedPromoCode(null)
    } else if (!disabled && orderObjectId) {
      setInitialCheckDone(false)
    }
  }, [disabled, appliedPromoCode, orderObjectId])

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value)
    setError('')
  }

  const handleRemoveClick = () => {
    // Don't show confirm dialog if remove is disabled
    if (disabled || isLoading || disableRemovePromo) {
      return
    }
    setShowConfirmDialog(true)
  }

  const handleConfirmRemove = async () => {
    if (
      disabled ||
      isLoading ||
      disableRemovePromo ||
      !appliedPromoCode ||
      !orderObjectId
    )
      return

    try {
      setIsLoading(true)
      setError('')

      await fetchData(removePromoCode(orderObjectId, appliedPromoCode))

      showToast(
        `Promo code "${appliedPromoCode}" removed successfully!`,
        'success',
      )

      setAppliedPromoCode(null)

      if (onPromoApplied) {
        onPromoApplied()
      }
    } catch (error: any) {
      console.error('Error removing promo code:', error)
      showToast(error.message || 'Failed to remove promo code')
    } finally {
      setIsLoading(false)
      setShowConfirmDialog(false)
    }
  }

  const handleCancelRemove = () => {
    setShowConfirmDialog(false)
  }

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setError('Please enter a promo code')
      return
    }

    const currentPromoCode = promoCode.trim().toUpperCase()

    // Check if promo code is already applied (case insensitive)
    if (
      appliedPromoCode &&
      currentPromoCode === appliedPromoCode.toUpperCase()
    ) {
      showToast(
        `Promo code "${appliedPromoCode}" has already been applied to this order`,
      )
      return
    }

    // Check if any promo code is already applied, show an error message and prevent adding another
    if (appliedPromoCode) {
      showToast(
        `Only one promo code can be used per order. "${appliedPromoCode}" is already applied.`,
      )
      return
    }

    try {
      setIsLoading(true)
      setError('')

      const codeToApply = promoCode.trim()
      await fetchData(applyPromoCode(orderObjectId, codeToApply))

      showToast(`Promo code "${codeToApply}" applied successfully!`, 'success')

      setPromoCode('')
      setAppliedPromoCode(codeToApply)

      // Fetch fresh order data to get the updated discount values
      try {
        const updatedOrderData = await fetchData<OrderData>(
          getOrderData(orderObjectId),
        )
        lastOrderDataRef.current = updatedOrderData
      } catch (error) {
        console.error('Error fetching updated order data:', error)
      }

      if (onPromoApplied) {
        onPromoApplied()
      }
    } catch (error: any) {
      console.error('Error applying promo code:', error)

      if (
        error.message &&
        (error.message.toLowerCase().includes('already') ||
          error.message.toLowerCase().includes('duplicate'))
      ) {
        try {
          const orderData = await fetchData<OrderData>(
            getOrderData(orderObjectId),
          )

          const existingPromoCode = orderData.discounts?.find(
            (discount) => discount.type === 'PromoCodeDiscount',
          )

          if (existingPromoCode) {
            const promoReason = existingPromoCode.reason || 'Unknown Promo'
            setAppliedPromoCode(promoReason)
            showToast(
              `Only one promo code allowed per order. "${promoReason}" is already applied.`,
            )
          } else {
            showToast(
              'Only one promo code or discount can be applied per order',
            )
          }
          setInitialCheckDone(false)
        } catch (subError) {
          console.error('Error verifying promo code status:', subError)
          showToast('Only one promo code can be applied per order')
          setInitialCheckDone(false)
        }
      } else {
        showToast(error.message || 'Failed to apply promo code')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (servicesData && !isLoading && lastOrderDataRef.current) {
      const updatedOrderData: OrderData = {
        ...lastOrderDataRef.current,
        totalDiscount: servicesData.totalDiscount,
        totalPromoCode: servicesData.totalPromoCode,
        estimatedTotal: servicesData.estimatedTotal,
      }

      // Only update orderServices if it's provided in servicesData
      if (servicesData.orderServices) {
        updatedOrderData.orderServices = servicesData.orderServices
      }

      lastOrderDataRef.current = updatedOrderData
    }
  }, [servicesData, isLoading])

  return (
    <div className={styles.promoCodeContainer}>
      <div className={styles.promoCodeTitle}>Promo Code</div>

      {showConfirmDialog && !disableRemovePromo && (
        <div className={styles.confirmDialogOverlay}>
          <div className={styles.confirmDialogContainer}>
            <div className={styles.confirmDialogTitle}>Remove Promo Code</div>
            <div className={styles.confirmDialogMessage}>
              Are you sure you want to remove the promo code "{appliedPromoCode}
              " from this order?
            </div>
            <div className={styles.confirmDialogButtons}>
              <SecondaryButton
                buttonName='Cancel'
                onClick={handleCancelRemove}
              />
              <PrimaryButton
                buttonName='Remove'
                onClick={handleConfirmRemove}
              >
                {isLoading ? (
                  <CircularProgress
                    size={20}
                    color='inherit'
                  />
                ) : (
                  'Remove'
                )}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {appliedPromoCode ? (
        <div className={styles.appliedPromoContainer}>
          <div className={styles.appliedPromoText}>
            <span className={styles.appliedPromoLabel}>
              Applied promo code:{' '}
            </span>
            <span className={styles.appliedPromoValue}>{appliedPromoCode}</span>
          </div>{' '}
          {/* Show promo code discount amount if available */}
          {(() => {
            const orderData = lastOrderDataRef.current
            if (!orderData || !orderData.discounts) return null

            const promo = orderData.discounts.find(
              (d: any) => d.type === 'PromoCodeDiscount',
            )
            if (!promo) return null

            // Get the discount amount from the backend
            let discountAmount = 0

            // Use totalDiscount from API if available
            if (
              typeof orderData.totalDiscount === 'number' &&
              !isNaN(orderData.totalDiscount)
            ) {
              discountAmount = Math.abs(orderData.totalDiscount)
            } else {
              // As a fallback, use a basic calculation
              // If it's a fixed amount discount
              if (promo.measure === '$' || promo.measure === '') {
                discountAmount = Number(promo.value)
              } else if (promo.measure === '%' && promo.value) {
                // For percentage discounts, use a simple calculation based on pre-tax total
                const percentDecimal = Number(promo.value) / 100
                const preTaxTotal = getPreTaxSubtotal(orderData)
                if (preTaxTotal > 0) {
                  discountAmount = preTaxTotal * percentDecimal
                }
              }
            }

            // Format the discounted amount as $
            const formattedDiscount = Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              signDisplay: 'never',
            }).format(discountAmount)

            // Make discount display negative
            const displayDiscount = `-${formattedDiscount}`

            // Simple display of the discount amount
            return (
              <div className={styles.discountDisplay}>
                <span>Discount: {displayDiscount}</span>
              </div>
            )
          })()}
          <button
            onClick={handleRemoveClick}
            className={styles.removeButton}
            aria-label='Remove promo code'
            title='Remove promo code'
            disabled={disabled || isLoading || disableRemovePromo}
          >
            ×
          </button>
        </div>
      ) : (
        <div className={styles.promoCodeInputContainer}>
          <TextField
            className={styles.promoCodeInput}
            value={promoCode}
            onChange={handlePromoCodeChange}
            placeholder='Enter promo code'
            disabled={disabled || isLoading}
            error={!!error}
            helperText={error}
            FormHelperTextProps={{
              className: styles.errorText,
            }}
          />
          <PrimaryButton
            buttonName='Apply'
            onClick={handleApplyPromoCode}
            disabled={disabled || isLoading || !promoCode.trim()}
          >
            {isLoading ? (
              <CircularProgress
                size={20}
                color='inherit'
              />
            ) : (
              'Apply'
            )}
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}

export default PromoCodeInput
